/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { Flex } from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import MccMncSelector from 'components/MccMncSelector'
import PhoneNumberoDirectorySelector from 'components/PhoneNumberoDirectorySelector'
import CustomerAccountSelector from 'components/CustomerAccountSelector'
import { MemoizedInput, MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import PhoneNumberReplacementServiceCreateMutation from './__mutation__/index.phoneNumberReplacementService.create.mutation'
import PhoneNumberReplacementServiceUpdateMutation from './__mutation__/index.phoneNumberReplacementService.update.mutation'
import PhoneNumberReplacementServiceReadQuery from './__query__/index.phoneNumberReplacementService.read.query'


/*
 * OBJECTS
 */
const Index = ({ isOpen, type, isCreateOnly, onClose, refetch, passOn }) => {
  // Const assignment.
  const _successFlags = React.useMemo(() => Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key), [])

  // Hook assignment.
  const [error, setError] = React.useState('')
  const [customerAccountId, setCustomerAccountId] = React.useState(void 0)
  const [mcc, setMcc] = React.useState(void 0)
  const [mnc, setMnc] = React.useState([])
  const [phoneNumberoDirectory, setPhoneNumberoDirectory] = React.useState(void 0)
  const [phoneNumbero, setPhoneNumbero] = React.useState([])
  const [percentageRate, setPercentageRate] = React.useState(void 0)
  const [MutationPhoneNumberReplacementServiceCreate, MutationPhoneNumberReplacementServiceCreateResponse] = useMutation(PhoneNumberReplacementServiceCreateMutation)
  const [MutationPhoneNumberReplacementServiceUpdate, MutationPhoneNumberReplacementServiceUpdateResponse] = useMutation(PhoneNumberReplacementServiceUpdateMutation)
  const _QueryPhoneNumberReplacementServiceRead = useQuery(PhoneNumberReplacementServiceReadQuery, {
    'variables': { 'phoneNumberoReplacementId': passOn?.phoneNumberReplacementServiceId ?? 'UN_KNOWN' },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'skip': isCreateOnly || !isOpen
  })

  // Object assignment.
  const _SubmitForm = async e => {
    // Local variable.
    let _customerAccountId, _mcc, _mnc, _phoneNumberoDirectory, _phoneNumbero

    // Prevent default behavior.
    e?.preventDefault()

    // Reset error.
    setError('')

    // Variable assignment.
    _customerAccountId = customerAccountId
    _mcc = mcc
    _mnc = mnc
    _phoneNumberoDirectory = phoneNumberoDirectory
    _phoneNumbero = phoneNumbero

    if (customerAccountId && (customerAccountId?.includes('(') || customerAccountId?.includes(')'))) _customerAccountId = customerAccountId.split('(')[1].split(')')[0]
    if (mcc && (mcc?.includes('(') || mcc?.includes(')'))) _mcc = mcc.split('(')[1].split(')')[0]
    if (mnc && 0 < mnc?.length) _mnc = _.compact(_.flatten(mnc.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))
    if (phoneNumberoDirectory && (phoneNumberoDirectory?.includes('(') || phoneNumberoDirectory?.includes(')'))) _phoneNumberoDirectory = phoneNumberoDirectory.split('(')[1].split(')')[0]
    if (phoneNumbero && 0 < phoneNumbero?.length) _phoneNumbero = _.compact(_.flatten(phoneNumbero.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))

    // Variable assignment.
    const _data = {
      type,
      'customerAccountIds': _.compact([_customerAccountId]),
      'mcc': _mcc,
      'mnc': _mnc,
      'phoneNumberoDirectoryId': _phoneNumberoDirectory,
      'phoneNumbero': _phoneNumbero,
      percentageRate
    }

    // Schema assignment for joi validation.
    const _JoiSchema = JoiBrowser.object({
      'customerAccountIds': JoiBrowser.array().items(JoiBrowser.string().required()).min(1).required(),
      'mcc': JoiBrowser.string().required(),
      'mnc': JoiBrowser.array().items(JoiBrowser.string().required()).min(1).required(),
      'phoneNumberoDirectoryId': JoiBrowser.string().required(),
      'phoneNumbero': JoiBrowser.array().items(JoiBrowser.string().required()).min(1).required(),
      'percentageRate': JoiBrowser.number().min(0).max(100).allow(null)
    }).options({ 'allowUnknown': true })

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_data)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    // If isCreateOnly is true then create new service else update existing service.
    if (isCreateOnly) {
      const _MutationPhoneNumberReplacementService = await MutationPhoneNumberReplacementServiceCreate({ 'variables': _data })

      if (_MutationPhoneNumberReplacementService instanceof Error) return _MutationPhoneNumberReplacementService

      toast(_MutationPhoneNumberReplacementService?.data?.PhoneNumberoReplacementCreate?.message)
      await refetch?.()

      onClose?.()
    } else if (!isCreateOnly) {
      const _MutationPhoneNumberReplacementService = await MutationPhoneNumberReplacementServiceUpdate({
        'variables': {
          'phoneNumberoReplacementId': _.isEmpty(passOn?.phoneNumberReplacementServiceIds) ? void 0 : passOn?.phoneNumberReplacementServiceId,
          'phoneNumberoReplacementIds': _.isEmpty(passOn?.phoneNumberReplacementServiceIds) ? void 0 : passOn?.phoneNumberReplacementServiceIds,
          ..._.omit(_data, 'customerAccountIds', 'vendorAccountIds')
        }
      })

      if (_MutationPhoneNumberReplacementService instanceof Error) return _MutationPhoneNumberReplacementService

      _MutationPhoneNumberReplacementService?.data?.PhoneNumberoReplacementUpdate?.map?.(i => toast(i?.message))
      await refetch?.()
      onClose?.()
    }

    // Return void 0.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    if (isCreateOnly) {
      setCustomerAccountId(void 0)
      setMcc(void 0)
      setMnc([])
      setPhoneNumberoDirectory(void 0)
      setPhoneNumbero([])
      setPercentageRate(void 0)
      setError('')
      return
    }

    // Update all states if data is available.
    if (!_.isEmpty(_QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementReadUnique) && _successFlags?.includes(_QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementReadUnique?.status)) {
      const _phoneNumberReplacementService = _QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementReadUnique

      setCustomerAccountId(_phoneNumberReplacementService?.CustomerAccount?.displayName ? `${_phoneNumberReplacementService?.CustomerAccount?.displayName} (${_phoneNumberReplacementService?.CustomerAccount?.id})` : void 0)
      setMcc(_.isEmpty(_phoneNumberReplacementService?.Mcc) ? void 0 : `${_phoneNumberReplacementService?.Mcc?.countryName} (${_phoneNumberReplacementService?.Mcc?.mcc})`)
      setMnc(_phoneNumberReplacementService?.Mnc?.map?.(j => `(${j?.mnc}) ${j?.network}`))
      setPhoneNumberoDirectory(_phoneNumberReplacementService?.PhoneNumberoDirectory?.displayName ? `${_phoneNumberReplacementService?.PhoneNumberoDirectory?.displayName} (${_phoneNumberReplacementService?.PhoneNumberoDirectory?.id})` : void 0)
      setPhoneNumbero(_phoneNumberReplacementService?.PhoneNumbero?.map?.(j => `${j?.phoneNumbero} (${j?.id})`))
      setPercentageRate(_phoneNumberReplacementService?.percentageRate)
    }
  }, [isCreateOnly, isOpen, passOn, _QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementReadUnique, _successFlags, type])

  // Const assignment.
  const _isLoading = MutationPhoneNumberReplacementServiceUpdateResponse.loading || MutationPhoneNumberReplacementServiceCreateResponse.loading
  const _isInputDisabled = _isLoading || _QueryPhoneNumberReplacementServiceRead?.loading

  // Return component.
  return (
    <form onSubmit={_SubmitForm}>
      <Flex>
        <Flex gap='22px' flexDir='column' w='100%'>
          <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
            <MemoizedSelect
              isRequired={true}
              name='accountType'
              label='Select Account'
              placeholder='e.g. "Account Type"'
              isInvalid={error?.includes('accountType')}
              error={error}
              options={Object.React.App.enums.CONTENT_TRANSLATION_TYPE.enums?.map(i => i.key)}
              data={type}
              disabled={true}
            />
            <CustomerAccountSelector
              isRequired={true}
              name='customerAccountId'
              label='Customer Account'
              placeholder='e.g. Spam Customer Account'
              iconColor='brand.500'
              error={error}
              customerType='WHOLESALE'
              isInvalid={error?.includes('customerAccountIds')}
              onChange={({ target }) => setCustomerAccountId(target?.value)}
              disabled={_isInputDisabled}
              data={customerAccountId}
            />
            <MemoizedInput
              disabled={_isInputDisabled}
              name='percentageRate'
              label='Load ( in Percentage )'
              placeholder='e.g. "10 is 10%"'
              type='number'
              isInvalid={error?.includes('percentageRate')}
              error={error}
              data={percentageRate}
              onChange={({ target }) => setPercentageRate('' === target.value ? void 0 : parseFloat(target.value, 10))}
            />
          </Flex>
          <Flex w='100%' gap='22px' flexDir={{ 'base': 'column', 'md': 'row' }}>
            <MccMncSelector
              isRequired={true}
              disabled={_isInputDisabled}
              mccValue={mcc}
              mncValue={mnc}
              inValidMnc={error?.includes('mnc')}
              inValidMcc={error?.includes('mcc')}
              onChange={i => {
                setMcc(i.mcc)
                setMnc(i.mnc)
              }}
            />
            <PhoneNumberoDirectorySelector
              isRequired={true}
              disabled={_isInputDisabled}
              phoneNumberoDirectoryValue={phoneNumberoDirectory ?? ''}
              phoneNumberoValue={phoneNumbero}
              inValidPhoneNumbero={error?.includes('phoneNumbero')}
              inValidPhoneNumberoDirectory={error?.includes('phoneNumberoDirectoryId')}
              onChange={i => {
                setPhoneNumbero(i.phoneNumbero)
                setPhoneNumberoDirectory(i.phoneNumberoDirectory)
              }}
            />
          </Flex>
        </Flex>
      </Flex>
      <SubmitButton
        defaultText={isCreateOnly ? 'Create Replacement ' : 'Update  Replacement '}
        disabled={_isInputDisabled}
        isLoading={_isLoading} />
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isOpen': PropTypes.bool,
  'isCreateOnly': PropTypes.bool,
  'type': PropTypes.string,
  'onClose': PropTypes.func,
  'refetch': PropTypes.func,
  'passOn': PropTypes.object
}
Index.defaultProps = {}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
